import { supabase } from '@/lib/supabase';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { webhookService } from './webhookService';
import { notificationService, notifyProjectFollowers } from './notificationService';
import { fieldTriggerService } from './fieldTriggerService';
import { followerAutomationService } from './followerAutomationService';

/**
 * Helper function to generate project link based on project type and pipeline
 * @param projectId - The project ID
 * @param projectType - The project type (Residential/Commercial)
 * @param pipelineId - The pipeline ID (optional)
 * @returns The relative project link path
 */
function getProjectLink(projectId: string, projectType?: string, pipelineId?: number | string): string {
  const pipelineRoute = projectType === 'Residential' || projectType === 'residential'
    ? 'pipeline-resi'
    : 'pipeline';

  // Include pipelineId if provided to open the correct pipeline
  const pipelineParam = pipelineId ? `pipelineId=${pipelineId}&` : '';
  return `/app/${pipelineRoute}?${pipelineParam}projectId=${projectId}&modal=true`;
}

export interface Project {
  id: string;
  project_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  system_size: number;
  system_cost: number;
  project_status: string;
  pipeline_id: string;
  current_stage_id: string;
  assigned_sales_rep_id: string;
  assigned_installer_partner_id: string;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

export interface CreateProjectInput {
  project_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  system_size: number;
  system_cost: number;
  pipeline_id: string;
  current_stage_id: string;
  assigned_sales_rep_id: string;
}

export interface UpdateProjectInput {
  project_name?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  system_size?: number;
  system_cost?: number;
  project_status?: string;
  current_stage_id?: string;
  assigned_sales_rep_id?: string;
  assigned_installer_partner_id?: string;
  last_updated_by_user?: string | null;
}

class ProjectService {
  async getProjects(): Promise<Project[]> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)  // Only get non-deleted projects
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as Project[];
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  async getProjectById(id: string): Promise<Project | null> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)  // Only get non-deleted projects
        .single();

      if (error) throw error;
      return data as unknown as Project;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  }

  async createProject(projectData: CreateProjectInput): Promise<Project> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('projects')
        .insert([{ ...projectData, tenant_id: tenantId }])
        .select()
        .single();

      if (error) throw error;

      // Auto-add assigned users as followers so they receive notifications
      const assignedUserIds = [
        data.assigned_sales_rep_id,
        data.assigned_internal_pm_id,
        data.assigned_installer_pm_id,
      ].filter((id): id is string => !!id);

      // Also add the creating user
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser?.id && !assignedUserIds.includes(currentUser.id)) {
        assignedUserIds.push(currentUser.id);
      }

      if (assignedUserIds.length > 0) {
        const followerRows = assignedUserIds.map(userId => ({
          related_entity_type: 'project',
          related_entity_id: data.id,
          project_id: data.id,
          user_id: userId,
          tenant_id: tenantId,
        }));

        await supabase
          .from('project_followers')
          .upsert(followerRows, { onConflict: 'related_entity_type,related_entity_id,user_id' })
          .then(({ error: followerError }) => {
            if (followerError) {
              console.error('Error auto-adding project followers:', followerError);
            }
          });
      }

      // Send webhook notification
      await webhookService.sendProjectCreated(data);

      // Send notification to project followers
      const pipelineUrl = '/app/pipeline-resi';
      await notifyProjectFollowers({
        projectId: data.id,
        type: 'project_created',
        projectName: data.project_name,
        senderId: data.assigned_sales_rep_id,
        message: `Project "${data.project_name}" has been created`,
        link: `${pipelineUrl}?projectId=${data.id}&modal=true`
      });

      // Trigger automations for project creation
      followerAutomationService.onProjectCreated(data).catch(err => {
        console.error('Error executing project creation automations:', err);
      });

      return data as unknown as Project;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  async updateProject(id: string, updates: UpdateProjectInput): Promise<Project> {
    try {
      const tenantId = await getCurrentTenantId();

      // Fetch current project state before updating (for field trigger comparison)
      const { data: oldProject } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .single();

      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;

      // Send webhook notification
      await webhookService.sendProjectUpdated(data);

      // Check if this is a stage change
      const isStageChange = oldProject &&
        updates.current_stage_id &&
        oldProject.current_stage_id !== updates.current_stage_id;

      const notificationMetadata: Record<string, any> = {
        pipelineId: data.pipeline_id
      };

      let notificationMessage = `Project "${data.project_name}" has been updated`;

      // If stage changed, fetch stage names and add to metadata
      if (isStageChange) {
        try {
          const { data: stagesData, error: stagesError } = await supabase
            .from('stages')
            .select('id, name')
            .in('id', [oldProject.current_stage_id, updates.current_stage_id]);

          if (!stagesError && stagesData) {
            const oldStage = stagesData.find(s => s.id === oldProject.current_stage_id);
            const newStage = stagesData.find(s => s.id === parseInt(updates.current_stage_id as string));

            notificationMetadata.old_stage = oldStage?.name || 'Unknown';
            notificationMetadata.new_stage = newStage?.name || 'Unknown';
            notificationMessage = `Project "${data.project_name}" moved from ${notificationMetadata.old_stage} to ${notificationMetadata.new_stage}`;
          }
        } catch (stageError) {
          console.error('Error fetching stage names for notification:', stageError);
        }
      }

      // Get current user info for sender_name
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { data: userData } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', currentUser.id)
          .single();

        if (userData) {
          notificationMetadata.sender_name = `${userData.first_name} ${userData.last_name}`.trim();
        }
      }

      // Send notification to project followers
      await notifyProjectFollowers({
        projectId: data.id,
        type: 'project_stage_move',
        projectName: data.project_name,
        projectType: data.project_type,
        senderId: data.assigned_sales_rep_id || currentUser?.id,
        senderName: notificationMetadata.sender_name,
        message: notificationMessage,
        metadata: notificationMetadata,
        link: getProjectLink(data.id, data.project_type, data.pipeline_id)
      });

      // Trigger automations for stage change
      if (isStageChange && oldProject) {
        followerAutomationService.onStageChanged(
          data,
          oldProject.current_stage_id,
          parseInt(updates.current_stage_id as string)
        ).catch(err => {
          console.error('Error executing stage change automations:', err);
        });
      }

      // Check field triggers for each updated field
      if (oldProject) {
        for (const [fieldName, newValue] of Object.entries(updates)) {
          const oldValue = (oldProject as any)[fieldName];

          // Only trigger if value actually changed
          if (fieldTriggerService.hasValueChanged(oldValue, newValue)) {
            // Call field trigger service asynchronously (non-blocking)
            fieldTriggerService.checkFieldTriggers({
              project_id: id,
              field_id: fieldName, // Use field name as ID for standard fields
              field_name: fieldName,
              old_value: oldValue,
              new_value: newValue,
            }).catch(err => {
              console.error(`Field trigger check failed for ${fieldName}:`, err);
            });
          }
        }
      }

      return data as unknown as Project;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  async deleteProject(id: string): Promise<void> {
    try {
      // Soft delete, through an RPC rather than a direct update.
      //
      // ⚠️ THIS CANNOT GO BACK TO `.update({ deleted_at })`. Deleted projects are
      // hidden by the `projects_select` policy (CLN-6.90), and Postgres requires
      // the row a statement writes to remain visible to its writer. A direct
      // update therefore fails with "new row violates row-level security policy"
      // for everyone except a superadmin. That exact regression shipped in
      // migration 20260820k7 and was rolled back the same hour.
      //
      // `soft_delete_project()` is SECURITY DEFINER, so the write does not pass
      // through the SELECT policy, and it authorizes the caller itself against
      // the same rule the UPDATE policy applies. It is idempotent: deleting an
      // already-deleted project succeeds.
      const { error } = await supabase.rpc('soft_delete_project', {
        p_project_id: id,
      });

      if (error) throw error;

      // Send webhook notification
      await webhookService.sendProjectDeleted({ id });

      // Send notification to project followers
      await notifyProjectFollowers({
        projectId: id,
        type: 'project_stage_move',
        message: `Project has been deleted`,
        link: `/app/pipeline-resi`
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  async getProjectsByPipeline(pipelineId: string): Promise<Project[]> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('pipeline_id', pipelineId)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)  // Only get non-deleted projects
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as Project[];
    } catch (error) {
      console.error('Error fetching projects by pipeline:', error);
      throw error;
    }
  }

  async getProjectsByStage(stageId: string): Promise<Project[]> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('current_stage_id', stageId)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)  // Only get non-deleted projects
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as Project[];
    } catch (error) {
      console.error('Error fetching projects by stage:', error);
      throw error;
    }
  }

  async getProjectsBySalesRep(salesRepId: string): Promise<Project[]> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('assigned_sales_rep_id', salesRepId)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)  // Only get non-deleted projects
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as Project[];
    } catch (error) {
      console.error('Error fetching projects by sales rep:', error);
      throw error;
    }
  }

  async moveProjectToStage(projectId: string, stageId: string): Promise<Project> {
    try {
      const tenantId = await getCurrentTenantId();

      // Fetch current project with old stage name
      const { data: currentProject } = await supabase
        .from('projects')
        .select(`
          *,
          old_stage:stages!current_stage_id(name)
        `)
        .eq('id', projectId)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)  // Only work with non-deleted projects
        .single();

      // Fetch new stage name
      const { data: newStageData } = await supabase
        .from('stages')
        .select('name')
        .eq('id', stageId)
        .single();

      const oldStageName = (currentProject as { old_stage?: { name?: string } } | null)?.old_stage?.name || 'Unknown';
      const newStageName = newStageData?.name || 'Unknown';

      // Update project stage
      const { data, error } = await supabase
        .from('projects')
        .update({ current_stage_id: stageId })
        .eq('id', projectId)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;

      // Send webhook notification
      await webhookService.sendProjectStageChanged(data);

      // Get current user for sender info
      const { data: { user } } = await supabase.auth.getUser();

      // Send notification to project followers
      await notifyProjectFollowers({
        projectId: data.id,
        type: 'project_stage_move',
        projectName: data.project_name,
        projectType: data.project_type,
        senderId: user?.id || data.assigned_sales_rep_id,
        senderName: user?.user_metadata?.full_name || user?.email || 'System',
        message: `Project "${data.project_name}" moved from ${oldStageName} to ${newStageName}`,
        metadata: {
          old_stage: oldStageName,
          new_stage: newStageName,
          pipelineId: data.pipeline_id
        },
        link: getProjectLink(data.id, data.project_type, data.pipeline_id)
      });

      // Trigger automations for stage change
      if (currentProject?.current_stage_id) {
        followerAutomationService.onStageChanged(
          data,
          currentProject.current_stage_id,
          parseInt(stageId)
        ).catch(err => {
          console.error('Error executing stage change automations:', err);
        });
      }

      return data as unknown as Project;
    } catch (error) {
      console.error('Error moving project to stage:', error);
      throw error;
    }
  }

  async assignSalesRep(projectId: string, salesRepId: string): Promise<Project> {
    try {
      const tenantId = await getCurrentTenantId();
      
      const { data, error } = await supabase
        .from('projects')
        .update({ assigned_sales_rep_id: salesRepId })
        .eq('id', projectId)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;

      // Send webhook notification
      await webhookService.sendProjectAssigned(data);

      // Send notification to project followers
      await notifyProjectFollowers({
        projectId: data.id,
        type: 'project_stage_move',
        projectName: data.project_name,
        senderId: data.assigned_sales_rep_id,
        message: `Project "${data.project_name}" has been assigned to a new sales rep`,
        metadata: {
          pipelineId: data.pipeline_id
        },
        link: getProjectLink(data.id, data.project_type, data.pipeline_id)
      });

      return data as unknown as Project;
    } catch (error) {
      console.error('Error assigning sales rep:', error);
      throw error;
    }
  }

  async assignInstallerPartner(projectId: string, installerPartnerId: string): Promise<Project> {
    try {
      const tenantId = await getCurrentTenantId();
      
      const { data, error } = await supabase
        .from('projects')
        .update({ assigned_installer_partner_id: installerPartnerId })
        .eq('id', projectId)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;

      // Send webhook notification
      await webhookService.sendInstallerAssigned(data);

      // Send notification to project followers
      await notifyProjectFollowers({
        projectId: data.id,
        type: 'project_stage_move',
        projectName: data.project_name,
        senderId: data.assigned_sales_rep_id,
        message: `Project "${data.project_name}" has been assigned to a new installer partner`,
        metadata: {
          pipelineId: data.pipeline_id
        },
        link: getProjectLink(data.id, data.project_type, data.pipeline_id)
      });

      return data as unknown as Project;
    } catch (error) {
      console.error('Error assigning installer partner:', error);
      throw error;
    }
  }

  async addPartnerUsersAsFollowers(projectId: string, partnerData: { id: string; name: string }): Promise<void> {
    try {
      const tenantId = await getCurrentTenantId();
      
      // Get all users from the partner organization
      const { data: partnerUsers, error: usersError } = await supabase
        .from('users')
        .select('id')
        .eq('installer_partner_id', partnerData.id)
        .eq('tenant_id', tenantId);

      if (usersError) {
        console.error('Error fetching partner users:', usersError);
        return;
      }

      if (!partnerUsers || partnerUsers.length === 0) {
        return;
      }

      // Add each partner user as a project follower
      const followerData = partnerUsers.map(user => ({
        user_id: user.id,
        related_entity_type: 'project',
        related_entity_id: projectId,
        tenant_id: tenantId,
        created_at: new Date().toISOString()
      }));

      const { error: followerError } = await supabase
        .from('project_followers')
        .upsert(followerData, { onConflict: 'user_id,related_entity_type,related_entity_id' });

      if (followerError) {
        console.error('Error adding partner users as followers:', followerError);
        return;
      }

    } catch (error) {
      console.error('Error adding partner users as followers:', error);
      throw error;
    }
  }
}

export const projectService = new ProjectService(); 